import { provide } from 'vue'
import {
  ASSETS,
  COUNTRIES,
  ALL_NIGERIAN_BANKS,
  LOCAL_COUNTRIES,
  COUNTRY_CODE_OPTIONS,
  GHS_MONEY_PROVIDER,
  XAF_MONEY_PROVIDERS,
  XOF_MONEY_PROVIDERS,
  PROVIDER,
  FIAT
} from '../utils/constants'
import { getPlaceholderPhoneNumber } from '../utils/helpers'

export default {
  data () {
    return {
      assets: ASSETS,
      ghanaianAccountNumberMinLength: 7,
      ghanaianAccountNumberMaxLength: 20,
      nigerianAccountNumber: 10,
      mobileMoneyAccountLengthMin: 9,
      mobileMoneyAccountLengthMax: 10,
      isAccountNameError: '',
      ghsMoneyProviders: GHS_MONEY_PROVIDER,
      allNigerianBanks: ALL_NIGERIAN_BANKS,
      countryCodeOptions: COUNTRY_CODE_OPTIONS,
      bankOptions: [],
      fiatCurrencies: FIAT
    }
  },
  methods: {
    addIntermediaryBankDetailsPayload (selectedBeneficiary) {
      if (selectedBeneficiary?.intermediaryBank) {
        const { bankAddress, bankName, reference, swiftCode } =
          selectedBeneficiary?.intermediaryBank
        return {
          bankName: bankName,
          bankAddress: bankAddress,
          swiftCode: swiftCode,
          reference: reference
        }
      }
      return null
    },
    getPayoutBeneficiaries (currency) {
      const payload = {
        page: 1,
        size: 1000,
        currency: currency
      }
      this.isLoading = true
      this.$store
        .dispatch('bulkPayout/getPayoutBeneficiaries', payload)
        .then()
        .catch((error) => {
          this.isLoading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    saveBeneficiary (selectedBeneficiary, selectedCurrency = '') {
      const currency = selectedCurrency || this.currency
      const hasMobileMoneySelected = selectedBeneficiary.isMobileMoney
      const personalDetailsArePresent = !!selectedBeneficiary.recipientName
      const personalDetails = {
        name: selectedBeneficiary.recipientName,
        country:
          selectedBeneficiary.recipientCountry ||
          selectedBeneficiary.country ||
          '',
        city:
          selectedBeneficiary.recipientCity || selectedBeneficiary.city || '',
        address: selectedBeneficiary?.recipientAddress || '',
        postalCode: selectedBeneficiary.recipientPostalCode || '',
        district: selectedBeneficiary.recipientDistrict || '',
        phoneNumber: selectedBeneficiary.recipientPhoneNumber || '',
        email: selectedBeneficiary.recipientEmail || ''
      }
      const intermediaryBank =
        this.addIntermediaryBankDetailsPayload(selectedBeneficiary)
      const payload = {
        destinationCurrency: currency,
        nickname: '',
        bankDetails: {
          bankCode: selectedBeneficiary.bankCode || '',
          accountNumber:
            selectedBeneficiary.accountNumber || selectedBeneficiary.accountNo,
          routingNumber: selectedBeneficiary.routingNo,
          swiftCode: selectedBeneficiary.swiftCode || '',
          bankName: selectedBeneficiary.bankName,
          accountName: selectedBeneficiary.accountName,
          country:
            selectedBeneficiary.bankCountry || selectedBeneficiary.countryCode,
          isWithinUs:
            selectedBeneficiary.bankCountry === this.$COUNTRY.US ? 'yes' : 'no',
          city: selectedBeneficiary.bankCity,
          bankAddress: selectedBeneficiary.bankAddress,
          district: selectedBeneficiary.district,
          postalCode: selectedBeneficiary.bankPostalCode,
          ...(hasMobileMoneySelected && {
            isMobileMoney: selectedBeneficiary.isMobileMoney
          })
        },
        intermediaryBank
      }
      const allPayloadDetails = { ...payload, personalDetails: personalDetails }
      this.isSavingBeneficiary = true
      this.$store
        .dispatch(
          'bulkPayout/saveBeneficiary',
          personalDetailsArePresent ? allPayloadDetails : payload
        )
        .then(() => {
          this.isSavingBeneficiary = false
          this.handleClose()
        })
        .catch((error) => {
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          this.$notify({
            title: this.httpErrorMessage.customMessage,
            message: err || '',
            type: 'error'
          })
        })
        .finally(() => {
          this.isSavingBeneficiary = false
        })
    },
    async getSupportedBanks () {
      const payload = {
        currency: this.currency,
        payoutType: this.isMobileMoneyPayoutMethod ? 'mno' : ''
      }
      this.loading = true
      await this.$store
        .dispatch('bulkPayout/getSupportedPayoutBanks', payload)
        .then(() => {
          this.loading = false
        })
        .catch((error) => {
          this.loading = false
          this.handleErrorResponseNotification(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    computeValidAccountNumberLength (accountNumberLength) {
      // Check if it's a mobile money payout method
      if (this.isMobileMoneyPayoutMethod) {
        return (
          accountNumberLength >= this.mobileMoneyAccountLengthMin &&
          accountNumberLength <= this.mobileMoneyAccountLengthMax
        )
      }
      // Check if it's for Ghanaian details
      if (this.isGhanianDetails) {
        const parsedLength = parseInt(accountNumberLength)
        return (
          parsedLength >= this.ghanaianAccountNumberMinLength &&
          parsedLength <= this.ghanaianAccountNumberMaxLength
        )
      }
      // For other cases (Nigerian details)
      return parseInt(accountNumberLength) === this.nigerianAccountNumber
    },
    async resolveAccountNameCore (number) {
      this.isLoading = true
      // resolve account name core endpoint call
      this.recipientInfoForm.accountName = '...'
      await this.$store
        .dispatch('terminal/setResolveAccountName', {
          account_number: number,
          bank_code: this.recipientInfoForm.bankCode,
          currency: this.currency
        })
        .then(() => {
          this.isLoading = false
          this.isAccountNameError = 'false'
          this.recipientInfoForm.accountName =
            this.$store.state.terminal.resolveAccountName
          // here, since nsano returns empty string as account name on dev,
          // we would allow manual account name input
          if (this.isOnDevEnvironment) {
            this.isAccountNameError = this.$STATUS.FALLBACK
          }
        })
        .catch((error) => {
          this.isLoading = false
          this.recipientInfoForm.accountName = ''
          const err = error.response
            ? error?.response?.data?.error?.publicMessage
            : error
          if (err) {
            const errorNotification = this.getErrorMessage(err)
            this.$notify.error(errorNotification)
          }
          this.isAccountNameError = this.$STATUS.FALLBACK
        })
    },
    getErrorMessage (errorMessage) {
      if (this.supportedBanksProviderIsZeepay) {
        return {
          title: 'Cannot fetch account name',
          message: 'Kindly input this manually',
          position: 'top-right'
        }
      } else {
        return {
          title: this.httpErrorMessage.customMessage,
          message: errorMessage,
          position: 'top-right'
        }
      }
    },
    remoteMethodBanks (query, country) {
      if (!country) {
        country = this.countryForCurrentCurrency
      }
      if (query.trim() !== '') {
        switch (country) {
          case this.$COUNTRY.NG:
          case this.$COUNTRY.GH:
          case this.$COUNTRY.CI:
          case this.$COUNTRY.CM:
            this.getSupportedBanks().then(() => {
              this.bankOptions = this.supportedBanks.filter((item) => {
                return item.name.toLowerCase().includes(query.toLowerCase())
              })
            })
            break
          default:
            this.bankOptions = []
            break
        }
      } else {
        this.bankOptions = []
      }
    },
    getAccountNumberLengthErrorMessage () {
      if (this.isMobileMoneyPayoutMethod) {
        return '9 - 10 digits required'
      } else if (this.isGhanianDetails) {
        return '7 - 20 digits required'
      } else {
        return '10 digits required'
      }
    },
    async validateAccountNo (rule, value, callback) {
      if (this.isLoading) return // Prevent multiple calls of this endpoint simultaneously
      if (!this.isFallBackAccountName) {
        callback() // No need to validate if not falling back to manual entry
        return
      }
      if (!this.computeValidAccountNumberLength(value.length)) {
        const errorMessage = this.getAccountNumberLengthErrorMessage()
        callback(new Error(errorMessage))
        return
      }
      if (!this.recipientInfoForm.bankName) {
        callback() // No need to validate if bank name is not provided
        return
      }
      // Only call the endpoint if there is no account name to prevent validation every time
      if (this.isAccountNameError !== 'false') {
        try {
          if (this.isMobileMoneyPayoutMethod) {
            await this.resolveMoneyProviderAccountName(value)
          } else {
            await this.resolveAccountName(value)
          }
          if (this.isAccountNameError === 'true') {
            callback(new Error('Please enter correct account details'))
          } else {
            callback()
          }
        } catch (error) {
          callback(new Error('Error occurred while validating account details'))
        }
      } else {
        callback()
      }
    },
    getNameInitials (name) {
      if (name) {
        const nameArray = name.split(' ')
        const lastName = nameArray[1] ? nameArray.pop().split('') : []
        const firstName = nameArray.shift().split('')

        return `${firstName[0]}${lastName.length ? lastName[0] : ''}`
      } else {
        return ''
      }
    },
    formatAccountName (name) {
      return name?.toLowerCase() || '--'
    }
  },
  computed: {
    singlePayoutDetails () {
      return this.$store.state?.bulkPayout?.currentBulkPayoutDetails || {}
    },
    selectedBeneficiary () {
      return this.$store.state.bulkPayout.beneficiaryDetails || {}
    },
    currency () {
      return this.$store.state.bulkPayout.payoutCurrency || ''
    },
    transferDestinationIsLocal () {
      const currencies = LOCAL_COUNTRIES.map((asset) => asset.currency)
      return currencies.includes(this.currency)
    },
    currencyIsGHS () {
      return this.currency === this.$CURRENCY.GHS
    },
    currencyIsXAF () {
      return this.currency === this.$CURRENCY.XAF
    },
    currencyIsXOF () {
      return this.currency === this.$CURRENCY.XOF
    },
    currencyIsUSD () {
      return this.currency === this.$CURRENCY.USD
    },
    currencyIsNGN () {
      return this.currency === this.$CURRENCY.NGN
    },
    francMoneyProviders () {
      let providers = []
      if (this.currencyIsXAF) {
        providers = XAF_MONEY_PROVIDERS
      } else if (this.currencyIsXOF) {
        providers = XOF_MONEY_PROVIDERS
      }
      return providers
    },
    mobileWalletSupportedCurrencies () {
      return this.currencyIsGHS || this.currencyIsXAF || this.currencyIsXOF
    },
    payoutCountry () {
      const details = COUNTRIES.find(
        (country) => country.currency === this.currency
      )
      return details
    },
    supportedBanksProviderIsZeepay () {
      return this.supportedBanksProvider === PROVIDER.ZEEPAY
    },
    ghanaCountryCode () {
      return this.countryCodeOptions.filter(
        (code) => code.value === this.$COUNTRY.GH
      )
    },
    formattedBankOptions () {
      // remove duplicates
      const seen = new Set()
      return this.bankOptions.filter((item) => {
        const key = item.name + item.code
        return seen.has(key) ? false : seen.add(key)
      })
    },
    cameroonCountryCode () {
      return this.countryCodeOptions.filter(
        (code) => code.value === this.$COUNTRY.CM
      )
    },
    civCountryCode () {
      return this.countryCodeOptions.filter(
        (code) =>
          code.value === this.$COUNTRY.CI || code.value === this.$COUNTRY.SN
      )
    },
    currentCountryCode () {
      const combinedArray = this.bankOptions
        .map((item) => {
          const matchingCountry = COUNTRY_CODE_OPTIONS.find(
            (option) => option.value === item.country
          )
          return matchingCountry
            ? { ...matchingCountry, providerName: item.name }
            : null
        })
        .filter(Boolean)

      return this.recipientInfoForm.bankName
        ? combinedArray.filter(
            (item) => item.providerName === this.recipientInfoForm.bankName
          )
        : []
    },
    isGhanianDetails () {
      return this.recipientInfoForm.bankCountry === this.$COUNTRY.GH
    },
    mobileMoneyPhoneNumberPlaceholder () {
      return getPlaceholderPhoneNumber(this.currency)
    },
    supportedBanks () {
      return Object.values(this.$store.state.bulkPayout.supportedPayoutBanks)[0]
    },
    supportedBanksProvider () {
      return Object.keys(this.$store.state.bulkPayout.supportedPayoutBanks)[0]
    },
    isFallBackAccountName () {
      return (
        this.isAccountNameError !== this.$STATUS.FALLBACK &&
        !this.currencyIsXAF &&
        !this.currencyIsXOF
      )
    },
    isSearchableBankDetails () {
      return (
        (this.recipientInfoForm.bankCountry === this.$COUNTRY.NG ||
          this.recipientInfoForm.bankCountry === this.$COUNTRY.GH) &&
        this.transferDestinationIsLocal
      )
    },
    isMobileMoneyPayoutMethod () {
      return (
        this.transferDestinationIsLocal &&
        this.$store.state.bulkPayout.paymentMethod === 'mno'
      )
    },
    isBankTransferPayoutMethod () {
      return (
        this.mobileWalletSupportedCurrencies &&
        this.$store.state.bulkPayout.paymentMethod === 'banks'
      )
    },
    currencyToCountryMap () {
      return {
        [this.$CURRENCY.NGN]: this.$COUNTRY.NG,
        [this.$CURRENCY.GHS]: this.$COUNTRY.GH,
        [this.$CURRENCY.XOF]: this.$COUNTRY.CI,
        [this.$CURRENCY.XAF]: this.$COUNTRY.CM
      }
    },
    countryForCurrentCurrency () {
      return this.currencyToCountryMap[this.currency]
    },
    currentMoneyProvider () {
      let data = []
      if (this.currencyIsGHS) {
        data = this.ghsMoneyProviders
      } else {
        data = this.francMoneyProviders
      }
      return data
    },
    allBeneficiaries () {
      return this.$store.state.bulkPayout.payoutBeneficiaries?.items || []
    },
    formattedCurrency () {
      const details = this.fiatCurrencies.find(
        (country) => country.currency === this.currency
      )
      return details ? details.symbol : ''
    },
    filterSavedBeneficiaries () {
      if (!this.search) return this.allBeneficiaries
      const searchTerm = this.search.toLowerCase()
      return this.allBeneficiaries.filter((beneficiary) => {
        const {
          name,
          details: { bankDetails },
          nickname
        } = beneficiary

        return [
          name,
          bankDetails.accountName,
          bankDetails.bankName,
          bankDetails.accountNumber,
          nickname
        ].some((field) => field?.toLowerCase().includes(searchTerm))
      })
    }
  }
}
